import StaticPageLayout from "./StaticPageLayout";

const PrivacyPolicy = () => {
  return (
    <StaticPageLayout
      title="Privacy Policy"
      subtitle="This Privacy Policy explains how Sundarban Cargo Services UK Limited collects, uses, stores and shares personal data when you use our website and courier/cargo services."
      lastUpdated="January 2026"
    >
      <div className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-10 space-y-10">
        <section>
          <h2 className="text-2xl font-semibold text-gray-900">Company Identification</h2>
          <p className="mt-3 text-gray-700 leading-relaxed">
            This website and our services are operated by <strong>Sundarban Cargo Services UK Limited</strong> (“we”,
            “our”, “us”), an international courier and cargo services provider based in the United Kingdom.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900">Personal Data We Collect</h2>
          <p className="mt-3 text-gray-700">We may collect and process the following categories of personal data:</p>
          <ul className="mt-4 list-disc pl-6 text-gray-700 space-y-2">
            <li>
              <strong>Identity & contact details</strong>: name, phone number, email address.
            </li>
            <li>
              <strong>Booking & shipment details</strong>: pickup and delivery addresses, package weight/dimensions,
              contents description, destination and tracking-related information.
            </li>
            <li>
              <strong>Billing information</strong>: invoicing details and transaction references.
            </li>
            <li>
              <strong>Website usage data</strong>: IP address, browser/device information, pages visited and general
              usage analytics.
            </li>
            <li>
              <strong>Communications</strong>: messages you send to us (for example, enquiries or support requests).
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900">How We Use Your Data</h2>
          <p className="mt-3 text-gray-700">We use personal data to:</p>
          <ul className="mt-4 list-disc pl-6 text-gray-700 space-y-2">
            <li>Provide quotes, create bookings and process shipments.</li>
            <li>Arrange pickup, delivery and tracking updates.</li>
            <li>Process payments and prevent fraud.</li>
            <li>Respond to enquiries and provide customer support.</li>
            <li>Comply with legal, tax, customs and regulatory requirements.</li>
            <li>Improve our website and services (for example, by analysing usage trends).</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900">Lawful Bases (UK GDPR)</h2>
          <p className="mt-3 text-gray-700">
            Under the UK GDPR, we process personal data on the following lawful bases as applicable:
          </p>
          <ul className="mt-4 list-disc pl-6 text-gray-700 space-y-2">
            <li>
              <strong>Contract</strong> – to provide courier/cargo services you request.
            </li>
            <li>
              <strong>Legal obligation</strong> – to meet tax, customs, and other regulatory requirements.
            </li>
            <li>
              <strong>Legitimate interests</strong> – to operate our business, improve services and prevent fraud.
            </li>
            <li>
              <strong>Consent</strong> – where we ask for it (for example, certain marketing communications).
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900">Payments (Stripe)</h2>
          <p className="mt-3 text-gray-700 leading-relaxed">
            Online payments are processed securely through <strong>Stripe</strong>. We do not store your card details
            on our servers. Stripe handles card processing and may process personal data in accordance with its
            privacy practices.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900">Sharing Your Data</h2>
          <p className="mt-3 text-gray-700">We may share relevant personal data with:</p>
          <ul className="mt-4 list-disc pl-6 text-gray-700 space-y-2">
            <li>
              <strong>Courier and logistics partners</strong> (for example, DHL, FedEx, TNT or similar carriers) to
              carry out pickup, transport, customs clearance and delivery.
            </li>
            <li>
              <strong>Payment processors</strong> (Stripe) to process payments and help prevent fraud.
            </li>
            <li>
              <strong>Authorities</strong> such as customs, law enforcement or regulators where required by law.
            </li>
            <li>
              <strong>Service providers</strong> supporting our operations (for example, hosting or IT support),
              under appropriate contractual protections.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900">International Transfers</h2>
          <p className="mt-3 text-gray-700 leading-relaxed">
            As we provide international courier and cargo services, your data may be transferred outside the UK to
            enable delivery and customs processing. Where required, we take steps to ensure appropriate safeguards
            are in place for international transfers.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900">Data Security</h2>
          <p className="mt-3 text-gray-700 leading-relaxed">
            We use appropriate technical and organisational measures to protect personal data against unauthorised
            access, loss, alteration or disclosure. Access to personal data is restricted to authorised personnel and
            trusted service providers who need it for business purposes.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900">Data Retention</h2>
          <p className="mt-3 text-gray-700 leading-relaxed">
            We retain personal data only for as long as necessary to provide our services and to meet legal, tax and
            regulatory requirements. Retention periods may vary depending on the type of data and the purpose for
            which it was collected.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900">Your Rights (UK GDPR)</h2>
          <p className="mt-3 text-gray-700">You have rights in relation to your personal data, including:</p>
          <ul className="mt-4 list-disc pl-6 text-gray-700 space-y-2">
            <li>The right to access your personal data.</li>
            <li>The right to rectify inaccurate or incomplete personal data.</li>
            <li>The right to request erasure of your personal data (in certain circumstances).</li>
            <li>The right to restrict processing (in certain circumstances).</li>
            <li>The right to data portability (where applicable).</li>
            <li>The right to object to processing based on legitimate interests or direct marketing.</li>
            <li>The right to withdraw consent at any time where processing is based on consent.</li>
            <li>The right to complain to the UK Information Commissioner’s Office (ICO).</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900">Contact</h2>
          <p className="mt-3 text-gray-700 leading-relaxed">
            For privacy-related enquiries or to exercise your rights, please contact:
          </p>
          <p className="mt-3 text-gray-900 font-semibold">support@sundarbancargo.co.uk</p>
        </section>
      </div>
    </StaticPageLayout>
  );
};

export default PrivacyPolicy;


